import { useEffect, useRef, useState, type RefObject } from 'react';

const VERTEX_SHADER = `attribute vec2 a_position;
varying vec2 v_uv;

void main() {
  v_uv = vec2(a_position.x * 0.5 + 0.5, 0.5 - a_position.y * 0.5);
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `precision highp float;

uniform sampler2D u_image;
uniform vec2 u_size;
uniform vec2 u_scale;
uniform vec2 u_offset;
uniform float u_time;
uniform vec4 u_ripples[24];
varying vec2 v_uv;

void main() {
  vec2 displacement = vec2(0.0);

  for (int i = 0; i < 24; i++) {
    vec4 ripple = u_ripples[i];
    float age = u_time - ripple.z;
    if (ripple.w > 0.0 && age >= 0.0 && age < 2.0) {
      vec2 delta = (v_uv - ripple.xy) * u_size;
      // Login car is much smaller than the yaxle.com hero; keep the same
      // wave shape but shrink radius + displacement so it reads similarly.
      float radius = (80.0 + age * 65.0) * 0.7;
      float distance = length(delta) / radius;
      vec2 direction = delta / max(length(delta), 16.0);
      float fade = (1.0 - smoothstep(0.0, 2.0, age)) * smoothstep(0.0, 0.2, age);
      float envelope = exp(-distance * distance * 2.5) * fade;
      float wave = sin(distance * 7.0 - age * 4.5);
      vec2 swirl = vec2(-direction.y, direction.x) * 0.08;
      displacement += (direction * wave + swirl) * envelope * 7.0;
    }
  }

  vec2 uv = (v_uv * u_scale) + u_offset;
  vec2 bend = (displacement / u_size) * u_scale;
  vec4 color = texture2D(u_image, uv + bend);
  color.r = texture2D(u_image, uv + bend * 1.08).r;
  color.b = texture2D(u_image, uv + bend * 0.92).b;
  gl_FragColor = vec4(color.rgb * color.a, color.a);
}
`;

const ELIGIBILITY_QUERIES = [
    '(prefers-reduced-motion: reduce)',
    '(pointer: coarse)',
    '(pointer: fine)',
    '(hover: hover)',
    '(min-width: 1024px)',
];

const rippleEligible = () => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const canHover = window.matchMedia('(hover: hover)').matches;
    const wideEnough = window.matchMedia('(min-width: 1024px)').matches;
    return !reducedMotion && !coarsePointer && finePointer && canHover && wideEnough;
};

interface UseCarGlassRippleOptions {
    imageUrl: string;
    enabled?: boolean;
    /** Bottom edge of this element is the top of the ripple zone. */
    anchorRef?: RefObject<HTMLElement | null>;
}

export const useCarGlassRipple = ({ imageUrl, enabled = true, anchorRef }: UseCarGlassRippleOptions) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hoverTargetRef = useRef<HTMLDivElement>(null);
    const [eligibilityEpoch, setEligibilityEpoch] = useState(0);

    useEffect(() => {
        const queries = ELIGIBILITY_QUERIES.map((query) => window.matchMedia(query));
        const onChange = () => setEligibilityEpoch((epoch) => epoch + 1);
        queries.forEach((query) => query.addEventListener('change', onChange));
        return () => queries.forEach((query) => query.removeEventListener('change', onChange));
    }, []);

    useEffect(() => {
        if (!enabled || !rippleEligible()) return;

        const canvas = canvasRef.current;
        const container = containerRef.current;
        const hoverTarget = hoverTargetRef.current;
        if (!canvas || !container || !hoverTarget) return;

        const gl = canvas.getContext('webgl', { alpha: true, antialias: false, depth: false });
        if (!gl) return;

        const createShader = (type: number, source: string): WebGLShader | null => {
            const shader = gl.createShader(type);
            if (!shader) return null;

            gl.shaderSource(shader, source);
            gl.compileShader(shader);

            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.warn('Glass ripple shader error:', gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }

            return shader;
        };

        const vertexShader = createShader(gl.VERTEX_SHADER, VERTEX_SHADER);
        const fragmentShader = createShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);

        if (!vertexShader || !fragmentShader) {
            gl.deleteShader(vertexShader);
            gl.deleteShader(fragmentShader);
            return;
        }

        const program = gl.createProgram();
        if (!program) {
            gl.deleteShader(vertexShader);
            gl.deleteShader(fragmentShader);
            return;
        }

        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.warn('Glass ripple program error:', gl.getProgramInfoLog(program));
            gl.deleteProgram(program);
            return;
        }

        const buffer = gl.createBuffer();
        const texture = gl.createTexture();

        gl.useProgram(program);
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

        const positionLoc = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(positionLoc);
        gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.uniform1i(gl.getUniformLocation(program, 'u_image'), 0);

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        const uniforms = {
            size: gl.getUniformLocation(program, 'u_size'),
            scale: gl.getUniformLocation(program, 'u_scale'),
            offset: gl.getUniformLocation(program, 'u_offset'),
            time: gl.getUniformLocation(program, 'u_time'),
            ripples: gl.getUniformLocation(program, 'u_ripples[0]'),
        };

        const ripples = new Float32Array(96);
        const startTime = performance.now();
        let rafId = 0;
        let textureReady = false;
        let isVisible = true;
        let containerWidth = 1;
        let containerHeight = 1;
        let rippleIndex = 0;
        let lastRippleTime = -10;
        let lastPointerX = -1000;
        let lastPointerY = -1000;
        let isHoveringTarget = false;

        const img = new Image();
        img.crossOrigin = 'anonymous';

        const offscreenCanvas = document.createElement('canvas');
        const offscreenCtx = offscreenCanvas.getContext('2d', { alpha: true });

        const hideCanvas = () => {
            canvas.style.opacity = '0';
        };

        const stopAnimation = () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = 0;
            }
            ripples.fill(0);
            lastRippleTime = -10;
            lastPointerX = lastPointerY = -1000;
            hideCanvas();
        };

        const renderStatic = () => {
            if (!textureReady || !isHoveringTarget || !isVisible || document.hidden) {
                hideCanvas();
                return;
            }
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.uniform1f(uniforms.time, 0);
            gl.uniform4fv(uniforms.ripples, ripples);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            canvas.style.opacity = '1';
        };

        const render = () => {
            rafId = 0;
            const currentTime = (performance.now() - startTime) / 1000;

            if (!textureReady || !isVisible || document.hidden || !isHoveringTarget) {
                stopAnimation();
                return;
            }

            if (currentTime - lastRippleTime > 2) {
                ripples.fill(0);
                lastRippleTime = -10;
                renderStatic();
                return;
            }

            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.uniform1f(uniforms.time, currentTime);
            gl.uniform4fv(uniforms.ripples, ripples);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            canvas.style.opacity = '1';

            rafId = requestAnimationFrame(render);
        };

        const updateSize = () => {
            stopAnimation();

            containerWidth = Math.max(1, container.clientWidth);
            containerHeight = Math.max(1, container.clientHeight);

            const dpr = Math.min(window.devicePixelRatio, 1.5, Math.sqrt(2500000 / (containerWidth * containerHeight)));
            canvas.width = Math.max(1, Math.round(containerWidth * dpr));
            canvas.height = Math.max(1, Math.round(containerHeight * dpr));

            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.uniform2f(uniforms.size, containerWidth, containerHeight);

            if (!img.naturalWidth) return;

            const imgAspect = img.naturalWidth / img.naturalHeight;
            const bgWidth = containerWidth;
            const bgHeight = bgWidth / imgAspect;
            
            const bgX = (containerWidth - bgWidth) / 2;
            const offsetVw = window.innerWidth * 0.12;
            const bgY = containerHeight - bgHeight + offsetVw;
            
            const scaleX = containerWidth / bgWidth;
            const scaleY = containerHeight / bgHeight;
            const offsetX = -bgX / bgWidth;
            const offsetY = -bgY / bgHeight;
            
            gl.uniform2f(uniforms.scale, scaleX, scaleY);
            gl.uniform2f(uniforms.offset, offsetX, offsetY);

            if (isHoveringTarget) renderStatic();
        };

        const loadTexture = () => {
            textureReady = false;
            stopAnimation();

            if (!img.complete || !img.naturalWidth || gl.isContextLost() || !offscreenCtx) return;

            const maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
            if (img.naturalWidth > maxSize || img.naturalHeight > maxSize) return;

            try {
                offscreenCanvas.width = img.naturalWidth;
                offscreenCanvas.height = img.naturalHeight;

                offscreenCtx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);

                offscreenCtx.drawImage(img, 0, 0);
                const originalData = offscreenCtx.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height);
                const originalPixels = originalData.data;

                // Match the panel's `brand.solid` so a semantic rebrand tints both cars.
                offscreenCtx.fillStyle = getComputedStyle(container).backgroundColor;
                offscreenCtx.fillRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);

                offscreenCtx.globalCompositeOperation = 'multiply';
                offscreenCtx.drawImage(img, 0, 0);

                offscreenCtx.globalCompositeOperation = 'source-over';

                const compositeData = offscreenCtx.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height);
                const data = compositeData.data;
                
                for (let i = 0; i < data.length; i += 4) {
                    const origR = originalPixels[i];
                    const origG = originalPixels[i + 1];
                    const origB = originalPixels[i + 2];
                    
                    const luminance = (0.299 * origR + 0.587 * origG + 0.114 * origB) / 255;
                    
                    if (luminance > 0.92) {
                        data[i + 3] = 0;
                    } else {
                        const alpha = data[i + 3] / 255;
                        data[i] = data[i] * alpha;
                        data[i + 1] = data[i + 1] * alpha;
                        data[i + 2] = data[i + 2] * alpha;
                    }
                }
                
                offscreenCtx.putImageData(compositeData, 0, 0);

                gl.bindTexture(gl.TEXTURE_2D, texture);
                // Shader UVs are top-origin (v = 0 at the top of the panel), matching
                // an unflipped canvas upload. Flipping here would invert the car.
                gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, offscreenCanvas);
                textureReady = true;
                updateSize();
            } catch {
                textureReady = false;
            }
        };

        const syncHoverTarget = () => {
            const anchor = anchorRef?.current;
            if (!anchor) return;
            const topOffset = anchor.getBoundingClientRect().bottom - container.getBoundingClientRect().top;
            hoverTarget.style.top = `${topOffset}px`;
        };

        const checkIfInHoverZone = (clientY: number): boolean => {
            syncHoverTarget();
            const targetRect = hoverTarget.getBoundingClientRect();
            return clientY >= targetRect.top;
        };

        const handlePointerMove = (e: PointerEvent) => {
            if (!textureReady || !isVisible || document.hidden) return;

            const inZone = checkIfInHoverZone(e.clientY);
            
            if (inZone && !isHoveringTarget) {
                isHoveringTarget = true;
                if (textureReady) {
                    renderStatic();
                }
            } else if (!inZone && isHoveringTarget) {
                isHoveringTarget = false;
                lastPointerX = lastPointerY = -1000;
                stopAnimation();
                return;
            }
            
            if (!isHoveringTarget) return;

            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const currentTime = (performance.now() - startTime) / 1000;

            const distance = Math.hypot(x - lastPointerX, y - lastPointerY);
            if (distance < 16) return;

            const timeSinceLastRipple = currentTime - lastRippleTime;
            if (timeSinceLastRipple < 0.08695652173913043) return;

            ripples.set([x / containerWidth, y / containerHeight, currentTime, 1], rippleIndex * 4);
            rippleIndex = (rippleIndex + 1) % 24;
            lastPointerX = x;
            lastPointerY = y;
            lastRippleTime = currentTime;

            if (!rafId) {
                rafId = requestAnimationFrame(render);
            }
        };

        const handlePointerLeave = () => {
            isHoveringTarget = false;
            lastPointerX = lastPointerY = -1000;
            stopAnimation();
        };

        const handleContextLost = (e: Event) => {
            e.preventDefault();
            textureReady = false;
            stopAnimation();
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopAnimation();
            } else if (isHoveringTarget) {
                renderStatic();
            }
        };

        const resizeObserver = new ResizeObserver(() => {
            syncHoverTarget();
            updateSize();
        });
        const intersectionObserver = new IntersectionObserver(([entry]) => {
            isVisible = entry.isIntersecting;
            if (!isVisible) stopAnimation();
        });

        resizeObserver.observe(container);
        if (anchorRef?.current) resizeObserver.observe(anchorRef.current);
        intersectionObserver.observe(container);
        syncHoverTarget();

        img.addEventListener('load', loadTexture);
        container.addEventListener('pointermove', handlePointerMove as EventListener);
        container.addEventListener('pointerleave', handlePointerLeave);
        canvas.addEventListener('webglcontextlost', handleContextLost);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        img.src = imageUrl;
        if (img.complete) {
            loadTexture();
        }

        return () => {
            stopAnimation();
            resizeObserver.disconnect();
            intersectionObserver.disconnect();
            img.removeEventListener('load', loadTexture);
            container.removeEventListener('pointermove', handlePointerMove as EventListener);
            container.removeEventListener('pointerleave', handlePointerLeave);
            canvas.removeEventListener('webglcontextlost', handleContextLost);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            gl.deleteTexture(texture);
            gl.deleteBuffer(buffer);
            gl.deleteProgram(program);
        };
    }, [imageUrl, enabled, eligibilityEpoch, anchorRef]);

    return { canvasRef, containerRef, hoverTargetRef };
};
